<template>
  <!-- 资金分布 -->
  <div class="capital-distribution-wrapper">
    <MoreTitle title="当日资金流向" class="top-title" @click="showMsg('capital')">
      <template v-slot:right>
        <i class="iconfont icon-tishishuoming" @click="showMsg('tenBrokers')"></i>
      </template>
    </MoreTitle>
    <div class="edge-padding">
      <div class="time-section">
        <span
          >{{ $t('更新时间') }}：{{ $dayjs(data.time ?? undefined).format('YYYY-MM-DD HH:mm') }}{{ $t(timeZone) }}</span
        >
        <span class="unit">{{ $t('单位') }}: {{ $t(divisorUnit[1]) }}</span>
      </div>
      <VueEcharts :option="option" />
      <div class="detail-section">
        <div class="capital-item">
          <div class="in-item in-title">
            <span>{{ $t('流入') }}:</span>
            <span class="value">{{ capitalInTotal }}</span>
          </div>
          <div class="out-item out-title" :style="{ 'margin-left': '16vw' }">
            <span>{{ $t('流出') }}:</span>
            <span class="value">{{ capitalOutTotal }}</span>
          </div>
        </div>
        <div v-for="item in capitalDetail" :key="item.label" class="capital-item">
          <span :style="{ 'margin-right': '20px', 'min-width': '30px' }">{{ item.label }}</span>
          <div class="in-item">
            <div class="bar-wrap">
              <span class="bar" :style="{ width: item.inWidth, background: item.inColor }"></span>
            </div>
            <span class="value-wrap" :style="{ color: item.inColor }">{{
              $digitjs(item.in).convert(isUs ? 4 : 2, divisorUnit[0])
            }}</span>
          </div>
          <div class="out-item">
            <div class="bar-wrap">
              <span class="bar" :style="{ width: item.outWidth, background: item.outColor }"></span>
            </div>
            <span class="value-wrap" :style="{ color: item.outColor }">{{
              $digitjs(item.out).convert(isUs ? 4 : 2, divisorUnit[0])
            }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import VueEcharts from '@/components/common/VueEcharts.vue'
import CapitalDistributionTip from './CapitalDistributionTip.vue'
import { getTimeZone } from '@/utils/utils'
import { showMsg } from '@/plugins/msg'
import theme from '@/theme'
import { cssColorGradient } from '@/utils/cssColor'
import { i18nTranslateInit } from '@/utils/utility-lang'
import { postcssPatch } from '@/utils/postcssPatch'

// const largeInColor = '#E73737'
// const bigInColor = '#FF4C4C'
// const midInColor = '#FA8181'
// const smallInColor = '#FFA3A3'
// const largeOutColor = '#24B670'
// const bigOutColor = '#32C77F'
// const midOutColor = '#5CDA9D'
// const smallOutColor = '#76EDB4'
// const flatColor = '#AAAAAA'

const toWhiteColor = '#eee'
const toBlackColor = '#000'
const largeInColor = cssColorGradient(theme['--up-color'], toBlackColor, 30)
const bigInColor = cssColorGradient(theme['--up-color'], toBlackColor, 10)
const midInColor = cssColorGradient(theme['--up-color'], toWhiteColor, 15)
const smallInColor = cssColorGradient(theme['--up-color'], toWhiteColor, 40)

const largeOutColor = cssColorGradient(theme['--down-color'], toBlackColor, 30)
const bigOutColor = cssColorGradient(theme['--down-color'], toBlackColor, 10)
const midOutColor = cssColorGradient(theme['--down-color'], toWhiteColor, 15)
const smallOutColor = cssColorGradient(theme['--down-color'], toWhiteColor, 40)
const flatColor = '#AAAAAA'

export default {
  name: 'CapitalDistribution',
  components: { VueEcharts, CapitalDistributionTip },
  inject: ['theme'],
  props: ['data', 'divisorUnit', 'market'],
  data() {
    return {
      tipVisible: false,
      useOpenCC: i18nTranslateInit.useOpenCC
    }
  },
  computed: {
    isUs() {
      return this.$route.query?.symbol?.includes('.us')
    },
    capitalInTotal() {
      return this.$digitjs(this.data.capitalInTotal).convert(this.isUs ? 4 : 2, this.divisorUnit[0])
    },
    capitalOutTotal() {
      return this.$digitjs(this.data.capitalOutTotal).convert(this.isUs ? 4 : 2, this.divisorUnit[0])
    },
    capitalNetIn() {
      const { capitalInTotal, capitalOutTotal } = this.data
      const capitalNetIn = capitalInTotal - capitalOutTotal
      return this.$digitjs(capitalNetIn).convert(2, this.divisorUnit[0])
    },
    distributionData() {
      const {
        capitalInLarge,
        capitalInBig,
        capitalInMid,
        capitalInSmall,
        capitalOutLarge,
        capitalOutBig,
        capitalOutMid,
        capitalOutSmall
      } = this.data

      return [
        {
          value: capitalOutLarge,
          name: this.useOpenCC('特大流出'),
          itemStyle: {
            color: largeOutColor
          }
        },
        {
          value: capitalOutBig,
          name: this.useOpenCC('大单流出'),
          itemStyle: {
            color: bigOutColor
          }
        },
        {
          value: capitalOutMid,
          name: this.useOpenCC('中单流出'),
          itemStyle: {
            color: midOutColor
          }
        },
        {
          value: capitalOutSmall,
          name: this.useOpenCC('小单流出'),
          itemStyle: {
            color: smallOutColor
          }
        },
        {
          value: capitalInSmall,
          name: this.useOpenCC('小单流入'),
          itemStyle: {
            color: smallInColor
          }
        },
        {
          value: capitalInMid,
          name: this.useOpenCC('中单流入'),
          itemStyle: {
            color: midInColor
          }
        },
        {
          value: capitalInBig,
          name: this.useOpenCC('大单流入'),
          itemStyle: {
            color: bigInColor
          }
        },
        {
          value: capitalInLarge,
          name: this.useOpenCC('特大流入'),
          itemStyle: {
            color: largeInColor
          }
        }
      ].filter((item) => item.value)
    },
    capitalDetail() {
      const {
        capitalInLarge,
        capitalInBig,
        capitalInMid,
        capitalInSmall,
        capitalOutLarge,
        capitalOutBig,
        capitalOutMid,
        capitalOutSmall
      } = this.data

      const inMax = Math.max(capitalInLarge, capitalInBig, capitalInMid, capitalInSmall) || 1
      const outMax = Math.max(capitalOutLarge, capitalOutBig, capitalOutMid, capitalOutSmall) || 1

      return [
        {
          label: this.$t('特大$1'),
          in: capitalInLarge,
          inWidth: `${((capitalInLarge / inMax) * 100).toFixed(2) || 1}%`,
          inColor: capitalInLarge ? largeInColor : flatColor,
          out: capitalOutLarge,
          outWidth: `${((capitalOutLarge / outMax) * 100).toFixed(2) || 1}%`,
          outColor: capitalOutLarge ? largeOutColor : flatColor
        },
        {
          label: this.$t('大单$1'),
          in: capitalInBig,
          inWidth: `${((capitalInBig / inMax) * 100).toFixed(2) || 1}%`,
          inColor: capitalInBig ? bigInColor : flatColor,
          out: capitalOutBig,
          outWidth: `${((capitalOutBig / outMax) * 100).toFixed(2) || 1}%`,
          outColor: capitalOutBig ? bigOutColor : flatColor
        },
        {
          label: this.$t('中单$1'),
          in: capitalInMid,
          inWidth: `${((capitalInMid / inMax) * 100).toFixed(2) || 1}%`,
          inColor: capitalInMid ? midInColor : flatColor,
          out: capitalOutMid,
          outWidth: `${((capitalOutMid / outMax) * 100).toFixed(2) || 1}%`,
          outColor: capitalOutMid ? midOutColor : flatColor
        },
        {
          label: this.$t('小单$1'),
          in: capitalInSmall,
          inWidth: `${((capitalInSmall / inMax) * 100).toFixed(2) || 1}%`,
          inColor: capitalInSmall ? smallInColor : flatColor,
          out: capitalOutSmall,
          outWidth: `${((capitalOutSmall / outMax) * 100).toFixed(2) || 1}%`,
          outColor: capitalOutSmall ? smallOutColor : flatColor
        }
      ]
    },
    timeZone() {
      // console.log(this.market)
      return getTimeZone(this.market)
    },
    option() {
      return {
        title: {
          text: this.useOpenCC('净流入'),
          subtext: this.capitalNetIn,
          left: 'center',
          top: '38%',
          textStyle: {
            color: this.theme['--font-color-3'],
            fontSize: this.$i18n.lang === 'en-us' ? postcssPatch(10) : postcssPatch(14)
          },
          subtextStyle: {
            color: this.theme['--font-color-1'],
            fontSize: this.$i18n.lang === 'en-us' ? postcssPatch(8) : postcssPatch(12)
          }
        },
        series: [
          {
            name: this.useOpenCC('资金类型'),
            type: 'pie',
            radius: ['40%', '70%'],
            center: ['50%', '50%'],
            label: {
              formatter: ({ percent }) => {
                return this.$digitjs(percent).unit('%').rounded()
              },
              alignTo: 'labelLine',
              borderWidth: 0,
              textBorderWidth: 0,
              color: this.theme['--font-color-1'],
              fontSize: 12
            },
            itemStyle: {
              borderColor: this.theme['--bg-color-2'],
              borderWidth: 2
            },
            data: this.distributionData
          }
        ]
      }
    }
  },
  methods: {
    showMsg
  }
}
</script>

<style lang="scss" scoped>
@keyframes toLightUpColor {
  from {
    background-color: var(--up-color);
  }
  to {
    background-color: #fff;
  }
}

@keyframes toLightDownColor {
  from {
    background-color: var(--down-color);
  }
  to {
    background-color: #fff;
  }
}

.capital-distribution-wrapper {
  background: var(--bg-color-2);
  .icon-tishishuoming {
    font-size: 13px;
    color: var(--font-color-3);
    font-weight: 400;
  }

  .top-title {
    height: 40px;
    margin-bottom: 10px;
  }
  .time-section {
    display: flex;
    align-items: flex-end;
    padding-bottom: 15px;
    line-height: 17px;
    font-size: 12px;
    color: var(--font-color-3);
    .unit {
      margin-left: auto;
    }
  }
  .detail-section {
    margin-top: 25px;

    .capital-item {
      display: flex;
      margin-bottom: 12px;
      font-size: 13px;
      .in-item,
      .out-item {
        display: flex;
        align-items: stretch;
        flex: 1;
        flex-shrink: 0;
        background: var(--bg-color-5);
        color: var(--font-color-2);
        &.in-title,
        &.out-title {
          background: var(--bg-color-2);
          margin-left: 0;
          padding: 0;
          flex: 1;
          .value {
            margin-left: 5px;
          }
        }
        &.out-title {
          margin-left: 60px;
        }
        .bar-wrap,
        .value-wrap {
          flex: 1;
          flex-shrink: 0;
          text-align: right;
          .bar {
            display: flex;
            height: 100%;
          }
        }
      }
      .in-item {
        .value {
          color: var(--up-color);
        }
      }
      .out-item {
        margin-left: 20px;
        .value {
          color: var(--down-color);
        }
      }
      &::before {
        //content: '';
        //display: flex;
        //width: 30px;
        //justify-content: flex-end;
      }
      &::after {
        content: '';
        display: flex;
      }
    }
  }
}
</style>
